import { useMemo, useState } from 'react';
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
  useDroppable,
  useDraggable,
} from '@dnd-kit/core';
import { Mic, Paperclip } from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import {
  Demanda,
  DemandaStatus,
  STATUS_LABELS,
  STATUS_OPTS,
  TIPO_LABELS,
  tipoVariant,
} from './demandaTypes';

function DropColumn({
  status,
  items,
  onOpen,
}: {
  status: DemandaStatus;
  items: Demanda[];
  onOpen: (d: Demanda) => void;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: status });
  return (
    <div
      ref={setNodeRef}
      className={`flex flex-col min-w-[240px] w-[260px] max-h-[calc(100vh-280px)] rounded-lg border bg-muted/30 ${
        isOver ? 'ring-2 ring-primary' : ''
      }`}
    >
      <div className="px-3 py-2 border-b flex items-center justify-between sticky top-0 bg-muted/50 backdrop-blur rounded-t-lg">
        <span className="text-sm font-semibold">{STATUS_LABELS[status]}</span>
        <Badge variant="outline">{items.length}</Badge>
      </div>
      <div className="flex-1 overflow-y-auto p-2 space-y-2">
        {items.map((d) => (
          <DemandaCard key={d.id} demanda={d} onOpen={onOpen} />
        ))}
        {!items.length ? (
          <p className="text-xs text-muted-foreground text-center py-6">Vazio</p>
        ) : null}
      </div>
    </div>
  );
}

function DemandaCard({ demanda, onOpen }: { demanda: Demanda; onOpen: (d: Demanda) => void }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: demanda.id,
    data: { demanda },
  });
  const style = transform
    ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)` }
    : undefined;
  const unread = demanda.conversation?.operatorUnreadCount ?? 0;

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...listeners}
      {...attributes}
      className={`rounded-md border bg-background p-3 shadow-sm cursor-grab active:cursor-grabbing relative ${
        isDragging ? 'opacity-40' : ''
      } ${unread > 0 ? 'border-primary/60 ring-1 ring-primary/30 bg-primary/5' : ''}`}
      onClick={() => onOpen(demanda)}
    >
      {unread > 0 ? (
        <span className="absolute -top-1.5 -right-1.5 min-w-[20px] h-5 px-1 rounded-full bg-primary text-primary-foreground text-[10px] font-bold flex items-center justify-center">
          {unread > 99 ? '99+' : unread}
        </span>
      ) : null}
      <div className="font-mono text-[10px] text-muted-foreground mb-1 flex items-center gap-1">
        <span>{demanda.protocolo}</span>
        {demanda.hasAudio ? <Mic className="w-3 h-3 text-muted-foreground" /> : null}
        {demanda.hasAttachment && !demanda.hasAudio ? (
          <Paperclip className="w-3 h-3 text-muted-foreground" />
        ) : null}
      </div>
      <div className="text-sm font-medium line-clamp-2 mb-2">{demanda.titulo}</div>
      <div className="flex flex-wrap gap-1 mb-2">
        <Badge variant={tipoVariant(demanda.tipo)}>{TIPO_LABELS[demanda.tipo] || demanda.tipo}</Badge>
        <Badge variant="outline">{demanda.prioridade}</Badge>
      </div>
      <div className="text-[11px] text-muted-foreground truncate">
        {demanda.company?.name || '—'} · {demanda.solicitanteNome || '—'}
      </div>
    </div>
  );
}

function CardPreview({ demanda }: { demanda: Demanda }) {
  return (
    <div className="rounded-md border bg-background p-3 shadow-lg w-[240px]">
      <div className="font-mono text-[10px] text-muted-foreground">{demanda.protocolo}</div>
      <div className="text-sm font-medium">{demanda.titulo}</div>
    </div>
  );
}

type Props = {
  columns: Array<{ status: DemandaStatus; items: Demanda[] }>;
  onMove: (id: string, status: DemandaStatus) => Promise<void>;
  onOpen: (d: Demanda) => void;
};

export function DemandasKanban({ columns, onMove, onOpen }: Props) {
  const [active, setActive] = useState<Demanda | null>(null);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } })
  );

  const byId = useMemo(() => {
    const map = new Map<string, Demanda>();
    for (const col of columns) for (const item of col.items) map.set(item.id, item);
    return map;
  }, [columns]);

  const ordered = STATUS_OPTS.map(
    (status) => columns.find((c) => c.status === status) ?? { status, items: [] }
  );

  const handleDragStart = (e: DragStartEvent) => {
    const d = byId.get(String(e.active.id));
    setActive(d ?? null);
  };

  const handleDragEnd = async (e: DragEndEvent) => {
    setActive(null);
    const overId = e.over?.id ? String(e.over.id) : null;
    if (!overId || !STATUS_OPTS.includes(overId as DemandaStatus)) return;
    const demanda = byId.get(String(e.active.id));
    if (!demanda || demanda.status === overId) return;
    await onMove(demanda.id, overId as DemandaStatus);
  };

  return (
    <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={(ev) => void handleDragEnd(ev)}>
      <div className="flex gap-3 overflow-x-auto pb-2">
        {ordered.map((col) => (
          <DropColumn key={col.status} status={col.status} items={col.items} onOpen={onOpen} />
        ))}
      </div>
      <DragOverlay>{active ? <CardPreview demanda={active} /> : null}</DragOverlay>
    </DndContext>
  );
}
